import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  Ticket,
  User,
  Book,
  FileText,
  HelpCircle,
  ChevronRight,
  MessageSquare,
  Monitor,
  Tablet,
  Smartphone,
} from 'lucide-react';
import { Spinner } from '@centraoneplus/ui';
import {
  portalConfigService,
  type PortalConfig,
} from '@services/portal-config.service';

type ViewportSize = 'desktop' | 'tablet' | 'mobile';

const viewportWidths: Record<ViewportSize, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
};

export default function PortalPreview() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<PortalConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewport, setViewport] = useState<ViewportSize>('desktop');

  useEffect(() => {
    portalConfigService
      .get()
      .then((result) => setConfig(result))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  const theme = config?.theme;
  const cssVars = useMemo(() => {
    if (!theme) return {};
    return {
      '--portal-primary': theme.primaryColor,
      '--portal-secondary': theme.secondaryColor,
      '--portal-accent': theme.accentColor,
      '--portal-bg': theme.backgroundColor,
      '--portal-text': theme.textColor,
      '--portal-header': theme.headerColor,
      '--portal-footer': theme.footerColor,
    } as React.CSSProperties;
  }, [theme]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-800">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 gap-4 dark:bg-gray-800">
        <p className="text-gray-600 dark:text-gray-400">No portal configuration found.</p>
        <button
          onClick={() => ((window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate('/settings'))}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Go Back
        </button>
      </div>
    );
  }

  const portalName = config.name || 'Help Center';
  const welcomeMessage =
    config.welcomeMessage ||
    'Search our knowledge base or browse categories to find the answers you need.';

  // Placeholder categories for preview
  const placeholderCategories = [
    { name: 'Getting Started', description: 'Learn the basics and set up your account', icon: Book, count: 12 },
    { name: 'Account & Billing', description: 'Manage your account settings and billing', icon: User, count: 8 },
    { name: 'Troubleshooting', description: 'Find solutions to common issues', icon: HelpCircle, count: 15 },
  ];

  // Placeholder articles for preview
  const placeholderArticles = [
    { title: 'How to reset your password', category: 'Getting Started' },
    { title: 'Understanding your billing cycle', category: 'Account & Billing' },
    { title: 'Setting up two-factor authentication', category: 'Getting Started' },
    { title: 'How to contact support', category: 'Troubleshooting' },
    { title: 'Updating your profile information', category: 'Account & Billing' },
    { title: 'Common error messages explained', category: 'Troubleshooting' },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col dark:bg-gray-800">
      {/* Preview Toolbar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0 z-50 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-center gap-3">
          <button
            onClick={() => ((window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate('/settings'))}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Settings
          </button>
          <div className="h-5 w-px bg-gray-300" />
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Portal Preview
          </span>
          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
            Preview Mode
          </span>
        </div>

        {/* Viewport Switcher */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 dark:bg-gray-800">
          {([
            { id: 'desktop', icon: Monitor, label: 'Desktop' },
            { id: 'tablet', icon: Tablet, label: 'Tablet' },
            { id: 'mobile', icon: Smartphone, label: 'Mobile' },
          ] as const).map((v) => (
            <button
              key={v.id}
              onClick={() => setViewport(v.id)}
              title={v.label}
              className={`p-2 rounded-md transition-colors ${
                viewport === v.id
                  ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-800 dark:text-gray-100'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'
              }`}
            >
              <v.icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      </div>

      {/* Preview Frame */}
      <div className="flex-1 overflow-auto flex justify-center p-4">
        <div
          className="bg-white shadow-xl rounded-lg overflow-hidden transition-all duration-300 flex flex-col dark:bg-gray-800"
          style={{
            width: viewportWidths[viewport],
            maxWidth: '100%',
            minHeight: 'calc(100vh - 80px)',
            ...cssVars,
          }}
        >
          {/* ===== PORTAL HEADER ===== */}
          <header
            className="border-b border-gray-200 sticky top-0 z-40 dark:border-gray-700"
            style={{ backgroundColor: theme?.headerColor || '#1F2937' }}
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="flex items-center justify-between h-16">
                {/* Logo / Name */}
                <div className="flex items-center gap-3">
                  {config.logoUrl ? (
                    <img
                      src={config.logoUrl}
                      alt={portalName}
                      className="h-8 object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className="text-lg font-bold text-white">
                      {portalName}
                    </span>
                  )}
                </div>

                {/* Search (desktop) */}
                {config.enableSearch && viewport !== 'mobile' && (
                  <div className="flex-1 max-w-lg mx-8">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                      <input
                        type="text"
                        placeholder="Search for help..."
                        disabled
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white/90 text-sm cursor-default dark:bg-gray-800 dark:border-gray-700"
                      />
                    </div>
                  </div>
                )}

                {/* Nav */}
                <nav className="flex items-center gap-3">
                  {config.enableTicketSubmission && (
                    <span className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-200 hover:text-white cursor-default">
                      <Ticket className="h-4 w-4" />
                      {viewport !== 'mobile' && <span>Submit a Request</span>}
                    </span>
                  )}
                  <span
                    className="px-4 py-1.5 text-sm font-medium text-white rounded-lg cursor-default"
                    style={{ backgroundColor: theme?.primaryColor }}
                  >
                    Sign In
                  </span>
                </nav>
              </div>
            </div>
          </header>

          {/* Mobile Search */}
          {config.enableSearch && viewport === 'mobile' && (
            <div className="bg-white border-b border-gray-200 px-4 py-3 dark:bg-gray-800 dark:border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search for help..."
                  disabled
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm cursor-default dark:border-gray-700"
                />
              </div>
            </div>
          )}

          {/* ===== HERO SECTION ===== */}
          <section
            className="text-white py-16"
            style={{
              background: `linear-gradient(135deg, ${theme?.primaryColor} 0%, ${theme?.secondaryColor || theme?.primaryColor} 100%)`,
            }}
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
              <h1 className="text-3xl md:text-4xl font-bold mb-4">
                How can we help you?
              </h1>
              <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
                {welcomeMessage}
              </p>

              {config.enableSearch && (
                <div className="max-w-2xl mx-auto">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      placeholder="Search for articles, guides, and more..."
                      disabled
                      className="w-full pl-12 pr-4 py-4 text-lg text-gray-900 rounded-xl shadow-lg cursor-default dark:text-gray-100"
                    />
                    <span
                      className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 text-white rounded-lg text-sm cursor-default"
                      style={{ backgroundColor: theme?.primaryColor }}
                    >
                      Search
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ===== CATEGORIES ===== */}
          <section
            className="py-16"
            style={{ backgroundColor: theme?.backgroundColor || '#FFFFFF' }}
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <h2
                className="text-2xl font-bold mb-1"
                style={{ color: theme?.textColor }}
              >
                Browse by Category
              </h2>
              <p className="text-gray-500 mb-8 dark:text-gray-400">Find help organized by topic</p>

              <div
                className={`grid gap-6 ${
                  viewport === 'mobile'
                    ? 'grid-cols-1'
                    : viewport === 'tablet'
                    ? 'grid-cols-2'
                    : 'grid-cols-3'
                }`}
              >
                {placeholderCategories.map((cat) => (
                  <div
                    key={cat.name}
                    className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow cursor-default dark:border-gray-700"
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                      style={{
                        backgroundColor: `${theme?.primaryColor}15`,
                      }}
                    >
                      <cat.icon
                        className="h-5 w-5"
                        style={{ color: theme?.primaryColor }}
                      />
                    </div>
                    <h3
                      className="font-semibold mb-1"
                      style={{ color: theme?.textColor }}
                    >
                      {cat.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-3 dark:text-gray-400">
                      {cat.description}
                    </p>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {cat.count} articles
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== FEATURED ARTICLES ===== */}
          <section className="py-16 bg-gray-50 dark:bg-gray-900">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2
                    className="text-2xl font-bold mb-1"
                    style={{ color: theme?.textColor }}
                  >
                    Featured Articles
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400">Most popular help articles</p>
                </div>
                <span
                  className="flex items-center gap-1 font-medium text-sm cursor-default"
                  style={{ color: theme?.primaryColor }}
                >
                  View all <ChevronRight className="h-4 w-4" />
                </span>
              </div>

              <div
                className={`grid gap-6 ${
                  viewport === 'mobile'
                    ? 'grid-cols-1'
                    : viewport === 'tablet'
                    ? 'grid-cols-2'
                    : 'grid-cols-3'
                }`}
              >
                {placeholderArticles.map((article) => (
                  <div
                    key={article.title}
                    className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow cursor-default dark:bg-gray-800 dark:border-gray-700"
                  >
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider dark:text-gray-500">
                      {article.category}
                    </span>
                    <h3
                      className="font-medium mt-2 mb-3"
                      style={{ color: theme?.textColor }}
                    >
                      {article.title}
                    </h3>
                    <span
                      className="text-sm font-medium flex items-center gap-1 cursor-default"
                      style={{ color: theme?.primaryColor }}
                    >
                      Read more <ChevronRight className="h-3 w-3" />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== CTA SECTION ===== */}
          {config.enableTicketSubmission && (
            <section
              className="py-16"
              style={{ backgroundColor: theme?.backgroundColor || '#FFFFFF' }}
            >
              <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <div
                  className="rounded-2xl p-8 md:p-12 text-center"
                  style={{
                    background: `linear-gradient(135deg, ${theme?.primaryColor}10 0%, ${theme?.primaryColor}20 100%)`,
                  }}
                >
                  <HelpCircle
                    className="h-12 w-12 mx-auto mb-4"
                    style={{ color: theme?.primaryColor }}
                  />
                  <h2
                    className="text-2xl font-bold mb-4"
                    style={{ color: theme?.textColor }}
                  >
                    Can't find what you're looking for?
                  </h2>
                  <p className="text-gray-600 mb-6 max-w-xl mx-auto dark:text-gray-400">
                    Our support team is here to help. Submit a request and we'll
                    get back to you as soon as possible.
                  </p>
                  <span
                    className="inline-flex items-center gap-2 px-6 py-3 text-white rounded-lg font-medium cursor-default"
                    style={{ backgroundColor: theme?.primaryColor }}
                  >
                    Submit a Request
                    <ChevronRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </section>
          )}

          {/* ===== FOOTER ===== */}
          <footer
            className="py-12 mt-auto"
            style={{
              backgroundColor: theme?.footerColor || '#111827',
              color: '#9CA3AF',
            }}
          >
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div
                className={`grid gap-8 ${
                  viewport === 'mobile' ? 'grid-cols-1' : 'grid-cols-4'
                }`}
              >
                <div>
                  <h3 className="text-white font-semibold mb-4">
                    {portalName}
                  </h3>
                  <p className="text-sm">
                    {config.footerText ||
                      'Find answers to common questions and get help with your account.'}
                  </p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-4">Quick Links</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="cursor-default hover:text-white transition-colors">Home</li>
                    {config.enableTicketSubmission && (
                      <li className="cursor-default hover:text-white transition-colors">
                        Submit a Request
                      </li>
                    )}
                    {config.enableTicketTracking && (
                      <li className="cursor-default hover:text-white transition-colors">
                        My Tickets
                      </li>
                    )}
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-4">Support</h3>
                  <ul className="space-y-2 text-sm">
                    {config.enableContactForm && (
                      <li className="cursor-default hover:text-white transition-colors">
                        Contact Us
                      </li>
                    )}
                    <li className="cursor-default hover:text-white transition-colors">
                      Email Support
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-4">Legal</h3>
                  <ul className="space-y-2 text-sm">
                    <li className="cursor-default hover:text-white transition-colors">
                      Privacy Policy
                    </li>
                    <li className="cursor-default hover:text-white transition-colors">
                      Terms of Service
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm">
                <p>
                  &copy; {new Date().getFullYear()} {portalName}. All rights
                  reserved.
                </p>
              </div>
            </div>
          </footer>

          {/* ===== CHAT WIDGET PLACEHOLDER ===== */}
          {config.enableChat && (
            <div
              className="fixed bottom-6 shadow-lg rounded-full p-4 cursor-default z-50"
              style={{
                backgroundColor: theme?.primaryColor,
                right: viewport === 'desktop' ? '24px' : '16px',
              }}
            >
              <MessageSquare className="h-6 w-6 text-white" />
            </div>
          )}
        </div>
      </div>

      {/* Custom CSS preview */}
      {config.customCss && <style>{config.customCss}</style>}
    </div>
  );
}
